import React from 'react';
import { useSelector } from 'react-redux';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Location, RentalState } from 'common/types';
import { Box, TextField, MenuItem, Typography } from '@mui/material';
import * as UserSelectors from 'selectors/UserSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { usePermissions } from 'hooks/usePermissions';

type InputEventType = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

interface Props {
	location: Location;
	onChange: (location: Location) => void;
	rentalState: RentalState;
	hasDelivery: boolean;
}

const StartAndEndLocation = (props: Props) => {
	const { location, hasDelivery } = props;
	const classes = useStyles();
	const { t } = useTranslation();
	const { hasPermission } = usePermissions();

	const shopLocations = useSelector(UserSelectors.userLocations);

	const renderLocationItems = (locations: Location[]) => {
		return locations.map((location) => (
			<MenuItem value={location.id} key={location.id}>
				<Typography variant="caption" className={classes.menuItem}>
					{location.name}
				</Typography>
			</MenuItem>
		));
	};

	const handleLocationChange = (e: InputEventType) => {
		const newLocation = shopLocations.find((location) => location.id === e.target.value);
		if (!newLocation) {
			return;
		}
		props.onChange(newLocation);
	};

	return (
		<Box>
			<TextField
				id="startLocation"
				variant="standard"
				label={t('common:shopLocation.store')}
				name="startLocation"
				select
				className={classes.textfield}
				value={location.id}
				onChange={handleLocationChange}
				margin="none"
				type="text"
				fullWidth
				inputProps={{
					className: classes.input,
				}}
				InputProps={{
					disableUnderline: true,
				}}
				disabled={
					props.rentalState === 'COMPLETED' || hasDelivery || !hasPermission('edit_booking')
				}
			>
				{renderLocationItems(shopLocations)}
			</TextField>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		textfield: {
			marginBottom: theme.spacing(1),
		},
		input: {
			fontSize: 12,
			color: theme.palette.common.black,
		},
		menuItem: {
			whiteSpace: 'break-spaces',
		},
	}),
);

export default StartAndEndLocation;
